// src/features/tidsregistering/components/attendance/TimeBlockBar.jsx
import React, { memo } from 'react';
import { Box, Typography, alpha } from '@mui/material';
import { format } from 'date-fns';
import LightTooltip from '../../../../components/common/LightTooltip';

const BAR_VERTICAL_PADDING = 6;

function TimeBlockTooltip({ slot }) {
  const isLive = !!slot.isLive;
  const startLabel = format(slot.startDate, 'HH:mm');
  const endLabel = isLive ? 'Nu' : format(slot.endDate, 'HH:mm');
  const codeTitle = slot.registrationCode?.title ?? '–';
  const hours = slot.originalDetail?.hours_sum != null
    ? Number(slot.originalDetail.hours_sum)
    : null;

  return (
    <Box sx={{ p: 1.5, minWidth: 140 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: slot.color, flexShrink: 0 }} />
        <Typography variant="subtitle2" sx={{ lineHeight: 1.2 }}>
          {codeTitle}
        </Typography>
        {isLive && (
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'success.main', animation: 'pulse 1.5s infinite' }} />
            <Typography variant="caption" color="success.main" fontWeight={600}>Live</Typography>
          </Box>
        )}
      </Box>
      <Typography variant="body2" fontWeight={500} color="text.primary">
        {startLabel} – {endLabel}
      </Typography>
      {hours != null && (
        <Typography variant="caption" color="text.secondary">
          {hours.toFixed(1)} timer
        </Typography>
      )}
    </Box>
  );
}

function TimeBlockBar({ slot, timeToX, rowHeight, onClick }) {
  if (slot.isBackground) return null;
  if (!slot.startDate || !slot.endDate) return null;

  const leftPx = timeToX(slot.startDate);
  const rightPx = timeToX(slot.endDate);
  const widthPx = Math.max(rightPx - leftPx - 1, 2);
  const topPx = BAR_VERTICAL_PADDING;
  const heightPx = rowHeight - BAR_VERTICAL_PADDING * 2;
  const isLive = !!slot.isLive;
  const startLabel = format(slot.startDate, 'HH:mm');
  const codeTitle = slot.registrationCode?.title ?? '';

  return (
    <LightTooltip
      title={<TimeBlockTooltip slot={slot} />}
      placement="top"
      arrow
      enterDelay={200}
    >
      <Box
        data-time-block="true"
        onClick={onClick ? () => onClick(slot) : undefined}
        sx={{
          position: 'absolute',
          left: leftPx,
          top: topPx,
          width: widthPx,
          height: heightPx,
          bgcolor: alpha(slot.color, isLive ? 0.35 : 0.8),
          border: `${isLive ? 2 : 1}px ${isLive ? 'dashed' : 'solid'} ${slot.color}`,
          borderRadius: 0.5,
          zIndex: 1,
          cursor: onClick ? 'pointer' : 'default',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          px: 0.5,
          '&:hover': { opacity: 0.85 },
        }}
      >
        {widthPx > 40 && (
          <Typography
            variant="caption"
            noWrap
            sx={{ fontSize: 10, color: 'common.white', fontWeight: 600, lineHeight: 1 }}
          >
            {codeTitle || startLabel}
          </Typography>
        )}
      </Box>
    </LightTooltip>
  );
}

export default memo(TimeBlockBar);
